import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { 
    collection, onSnapshot, doc, getDoc, updateDoc, 
    increment, serverTimestamp, arrayUnion, query, 
    orderBy, addDoc, where 
} from 'firebase/firestore';

const Tasks = ({ onBack }) => {
    const [loading, setLoading] = useState(false);
    const [isClaimed, setIsClaimed] = useState(false);
    const [memberType, setMemberType] = useState('Free');
    const [tasks, setTasks] = useState([]);
    const [doneTasks, setDoneTasks] = useState([]);
    const [activeTab, setActiveTab] = useState('available');
    const [history, setHistory] = useState([]);
    const [selectedTask, setSelectedTask] = useState(null);
    const [proofImg, setProofImg] = useState(null);

    useEffect(() => {
        if (!auth.currentUser) return;

        const fetchUserData = async () => {
            try {
                const userRef = doc(db, "users", auth.currentUser.uid);
                const docSnap = await getDoc(userRef);
                if (docSnap.exists()) {
                    const userData = docSnap.data();
                    setMemberType(userData.memberType || 'Free');
                    setDoneTasks(userData.doneTasks || []);
                    if (userData.lastClaim) {
                        const lastClaim = userData.lastClaim.toDate();
                        const now = new Date();
                        if (now - lastClaim < 24 * 60 * 60 * 1000) setIsClaimed(true);
                    }
                }
            } catch (err) { console.error("User fetch error:", err); }
        };

        const unsubTasks = onSnapshot(collection(db, "tasks"), (snapshot) => {
            setTasks(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const qHistory = query(
            collection(db, "task_submissions"), 
            where("userId", "==", auth.currentUser.uid)
        );
        const unsubHistory = onSnapshot(qHistory, (snapshot) => {
            setHistory(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        fetchUserData();
        return () => { unsubTasks(); unsubHistory(); };
    }, []);

    const handleDailyClaim = async () => {
        if (isClaimed || loading) return;
        setLoading(true);
        try {
            const userRef = doc(db, "users", auth.currentUser.uid);
            const reward = memberType.toLowerCase() === 'premium' ? 30 : 10;
            await updateDoc(userRef, {
                points: increment(reward),
                lastClaim: serverTimestamp()
            });
            setIsClaimed(true);
            alert(`${reward} Points ရရှိပါပြီ!`);
        } catch (err) { alert(err.message); }
        setLoading(false);
    };

    const handleTaskAction = (task) => {
        const isLocked = task.memberLevel === 'Premium' && memberType.toLowerCase() !== 'premium';
        if (isLocked) return alert("Premium Member များသာ လုပ်ဆောင်နိုင်ပါသည်။");
        window.open(task.link, '_blank');
        setSelectedTask(task);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => setProofImg(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const submitProof = async () => {
        if (!proofImg || loading) return alert("Screenshot အရင်တင်ပါ");
        setLoading(true);
        try {
            await addDoc(collection(db, "task_submissions"), {
                userId: auth.currentUser.uid,
                userEmail: auth.currentUser.email,
                taskId: selectedTask.id,
                taskTitle: selectedTask.title,
                points: selectedTask.points,
                screenshot: proofImg,
                status: 'pending',
                createdAt: serverTimestamp()
            });
            alert("တင်သွင်းပြီးပါပြီ။ Admin အတည်ပြုချက်ကို စောင့်ပါ။");
            setSelectedTask(null);
            setProofImg(null);
        } catch (err) { alert(err.message); }
        setLoading(false);
    };

    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <span className="material-icons-round" onClick={onBack} style={styles.backIcon}>arrow_back</span>
                <h3 style={styles.headerTitle}>Task Center</h3>
            </div>

            {/* Daily Bonus Card */}
            <div style={styles.attendanceCard}>
                <h4 style={{ margin: '0 0 10px', fontSize: '16px', color: '#8b949e' }}>Daily Attendance</h4>
                <button 
                    onClick={handleDailyClaim} 
                    disabled={isClaimed || loading} 
                    style={isClaimed ? styles.disabledBtn : styles.activeBtn}
                >
                    {isClaimed ? "Already Claimed ✅" : loading ? "Wait..." : "Claim Daily Points"}
                </button>
            </div>

            {/* Tab Switcher */}
            <div style={styles.tabContainer}>
                <div onClick={() => setActiveTab('available')} style={activeTab === 'available' ? styles.activeTabBtn : styles.tabBtn}>Tasks</div>
                <div onClick={() => setActiveTab('history')} style={activeTab === 'history' ? styles.activeTabBtn : styles.tabBtn}>History</div>
            </div>

            {activeTab === 'available' ? (
                <div>
                    {tasks.map(task => {
                        const isSubmitted = history.some(h => h.taskId === task.id && h.status === 'pending');
                        const isCompleted = doneTasks.includes(task.id);
                        const isLocked = task.memberLevel === 'Premium' && memberType.toLowerCase() !== 'premium';
                        
                        return (
                            <div key={task.id} style={{ ...styles.taskItem, opacity: (isCompleted || isSubmitted) ? 0.6 : 1 }}>
                                <div style={styles.iconBox}>
                                    <span className="material-icons-round" style={{ color: isLocked ? '#444' : '#D4AF37' }}>
                                        {isLocked ? 'lock' : 'bolt'}
                                    </span>
                                </div>
                                <div style={{ flex: 1, marginLeft: '15px' }}>
                                    <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#fff' }}>{task.title}</div>
                                    <div style={{ color: '#D4AF37', fontSize: '12px', fontWeight: 'bold' }}>+{task.points} Pts</div>
                                </div>

                                {isCompleted ? (
                                    <span style={styles.doneBadge}>Done ✅</span>
                                ) : isSubmitted ? (
                                    <span style={styles.pendingBadge}>Pending ⏳</span>
                                ) : (
                                    <button onClick={() => handleTaskAction(task)} style={isLocked ? styles.lockBtn : styles.goBtn}>
                                        {isLocked ? 'Locked' : 'Go Task'}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                /* History Tab */
                <div>
                    {history.length === 0 ? (
                        <div style={{textAlign:'center', padding:'40px', color:'#8b949e'}}>No records.</div>
                    ) : (
                        history.sort((a, b) => b.createdAt - a.createdAt).map(item => (
                            <div key={item.id} style={styles.historyItem}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#fff' }}>{item.taskTitle}</div>
                                    <div style={{ fontSize: '11px', color: '#8b949e', marginTop: '4px' }}>
                                        {item.createdAt?.toDate().toLocaleString()}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ color: '#D4AF37', fontWeight: 'bold' }}>+{item.points} Pts</div>
                                    <div style={styles.statusBadge(item.status)}>{item.status.toUpperCase()}</div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Submit Modal */}
            {selectedTask && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalCard}>
                        <h4 style={{ color: '#fff', marginBottom: '10px' }}>Upload Proof</h4>
                        <div onClick={() => document.getElementById('fileInput').click()} style={styles.uploadArea}>
                            {proofImg ? (
                                <img src={proofImg} style={{ width: '100%', maxHeight: '200px', objectFit: 'contain' }} alt="proof" />
                            ) : (
                                <div style={{color: '#8b949e'}}>
                                    <span className="material-icons-round" style={{fontSize: '40px'}}>add_a_photo</span>
                                    <br/>Upload Screenshot
                                </div>
                            )}
                            <input id="fileInput" type="file" accept="image/*" hidden onChange={handleFileChange} />
                        </div>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                            <button onClick={submitProof} disabled={loading || !proofImg} style={styles.submitBtn}>
                                {loading ? "Wait..." : "Submit"}
                            </button>
                            <button onClick={() => { setSelectedTask(null); setProofImg(null); }} style={styles.cancelBtn}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- Styles (Dark Theme - Admin Panel Color Palette) ---
const styles = {
    container: { 
        padding: '20px', 
        background: '#0a0c10', 
        minHeight: '100vh', 
        color: '#c9d1d9',
        fontFamily: "'Inter', sans-serif"
    },
    header: { display: 'flex', alignItems: 'center', marginBottom: '25px' },
    backIcon: { cursor: 'pointer', fontSize: '28px', color: '#8b949e' },
    headerTitle: { marginLeft: '15px', fontWeight: '800', color: '#fff' },
    
    attendanceCard: { 
        background: '#161b22', 
        padding: '20px', 
        borderRadius: '20px', 
        textAlign: 'center', 
        border: '1px solid #30363d',
        marginBottom: '20px'
    },
    activeBtn: { 
        background: '#D4AF37', 
        color: '#000', 
        border: 'none', 
        padding: '14px', 
        borderRadius: '12px', 
        width: '100%', 
        fontWeight: 'bold', 
        cursor: 'pointer' 
    },
    disabledBtn: { 
        background: '#30363d', 
        color: '#8b949e', 
        border: 'none', 
        padding: '14px', 
        borderRadius: '12px', 
        width: '100%', 
        fontWeight: 'bold' 
    },

    tabContainer: { display: 'flex', background: '#161b22', padding: '5px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #30363d' },
    tabBtn: { flex: 1, textAlign: 'center', padding: '10px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', color: '#8b949e' },
    activeTabBtn: { flex: 1, textAlign: 'center', padding: '10px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', background: '#30363d', color: '#D4AF37', fontWeight: 'bold' },

    taskItem: { 
        display: 'flex', 
        alignItems: 'center', 
        padding: '15px', 
        background: '#11141b', 
        borderRadius: '16px', 
        marginBottom: '12px', 
        border: '1px solid #21262d' 
    },
    iconBox: { width: '48px', height: '48px', background: '#0a0c10', borderRadius: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '1px solid #30363d' },
    
    goBtn: { background: '#D4AF37', color: '#000', border: 'none', padding: '8px 16px', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold' },
    lockBtn: { background: '#21262d', color: '#484f58', border: 'none', padding: '8px 16px', borderRadius: '10px', fontWeight: 'bold' },
    
    doneBadge: { color: '#238636', fontSize: '12px', fontWeight: 'bold' },
    pendingBadge: { color: '#D4AF37', fontSize: '12px', fontWeight: 'bold' },

    historyItem: { 
        display: 'flex', 
        alignItems: 'center', 
        padding: '15px', 
        background: '#161b22', 
        borderRadius: '16px', 
        marginBottom: '10px', 
        border: '1px solid #30363d' 
    },
    statusBadge: (status) => ({ 
        fontSize: '10px', 
        fontWeight: 'bold', 
        marginTop: '4px', 
        color: status === 'approved' ? '#238636' : status === 'pending' ? '#D4AF37' : '#da3633' 
    }),

    modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalCard: { background: '#161b22', padding: '25px', borderRadius: '24px', width: '85%', maxWidth: '380px', textAlign: 'center', border: '1px solid #30363d' },
    uploadArea: { border: '2px dashed #30363d', borderRadius: '16px', padding: '20px', background: '#0d1117', cursor: 'pointer' },
    submitBtn: { flex: 1, background: '#D4AF37', color: '#000', border: 'none', padding: '12px', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer' },
    cancelBtn: { flex: 1, background: 'transparent', color: '#8b949e', border: 'none', padding: '12px', borderRadius: '12px', cursor: 'pointer' }
};

export default Tasks;